import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { destinationFor, droppableId, type DroppableData } from "@/dnd/ids";
import { cnTransferDropTargetSurface } from "@/dnd/transferDropTargetStyles";
import { useTransferDropHighlight } from "@/dnd/useTransferDropHighlight";
import { RailDisclosureIcon } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailChevron";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import {
  useRailRowContextMenu,
  type RailRowMenuController,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import type { Canvas, Folder } from "@cohesive/api-client";
import { useDroppable } from "@dnd-kit/core";
import {
  Folder as FolderIcon,
  FolderOpen,
  FolderPlus,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";

/** Actions surfaced on a folder row's kebab menu. */
export interface CanvasFolderActions {
  /** Navigate to the folder's view on the workspace detail page. */
  onOpen: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onNewSubfolder: (parentFolderId: string) => void;
  /** Create the folder's resource kind (a canvas / a chat) directly inside it. */
  onNewResource: (folderId: string) => void;
}

/**
 * Three-dot actions menu for canvas folder rows. Mirrors the files-rail folder
 * kebab — same `RailRowMenu`, kebab placement, and the design-system item
 * defaults — minus the file-only actions (Upload / Move to), so the canvas
 * rail matches the files rail.
 */
function CanvasFolderMenu({
  controller,
  label,
  onOpen,
  onNewCanvas,
  onNewSubfolder,
  onRename,
  onDelete,
}: {
  controller: RailRowMenuController;
  label: string;
  onOpen: () => void;
  onNewCanvas: () => void;
  onNewSubfolder: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <RailRowMenu {...railRowMenuProps(controller)} label={label}>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        <FolderOpen />
        Open
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onNewCanvas, 0);
        }}
      >
        <Plus />
        New canvas
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onNewSubfolder, 0);
        }}
      >
        <FolderPlus />
        New folder
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onRename, 0);
        }}
      >
        <Pencil />
        Rename
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        danger
        onClick={(e) => {
          e.stopPropagation();
          window.setTimeout(onDelete, 0);
        }}
      >
        <Trash2 />
        Archive
      </DropdownMenuItem>
    </RailRowMenu>
  );
}

/**
 * Drop target for a canvas folder (or the workspace root when `folderId` is
 * null). Dropping a canvas card here re-files it — a move (§5.4: the rail is an
 * organizing surface). Uses the shared transfer drop-target highlight, same as
 * [`FilesRailDropTarget`].
 */
export function CanvasFolderDropTarget({
  workspaceId,
  folderId,
  children,
  className,
  dropId: dropIdOverride,
}: {
  workspaceId: string;
  folderId: string | null;
  children: React.ReactNode;
  className?: string;
  /** Distinct droppable id for off-rail mounts (e.g. detail-page folder
   *  cards) — dnd-kit ids must be unique, and the rail may show the same
   *  folder at the same time. Destination semantics are unchanged. */
  dropId?: string;
}) {
  const dropId =
    dropIdOverride ?? droppableId.railCanvasFolder(workspaceId, folderId);

  const droppableData = useMemo(
    (): DroppableData => ({
      surface: "rail-canvas-folder",
      destination: destinationFor("rail-canvas-folder", {
        workspaceId,
        folderId,
      }),
    }),
    [workspaceId, folderId],
  );

  const droppable = useDroppable({ id: dropId, data: droppableData });
  // Organizing surface: active for every drag. A canvas shows a live "Move"
  // ring; a file/chat shows the grayed "can't move here" ring — never inert, so
  // the user always gets clear feedback over the Canvases section.
  const { isOver, disallowed } = useTransferDropHighlight(
    dropId,
    droppableData.destination,
  );

  return (
    <div
      ref={droppable.setNodeRef}
      className={cnTransferDropTargetSurface({ isOver, disallowed, className })}
    >
      {children}
    </div>
  );
}

/**
 * Expandable canvas folder row. Mirrors [`FilesRailFolderRow`]: the header is a
 * drop target, leads with an always-visible disclosure chevron beside the
 * folder icon, and children render indented when open. The hover-reveal
 * group scopes to the header row only (`group/row`), so hovering a child row
 * never reveals this folder's kebab.
 */
function CanvasFolderRow({
  workspaceId,
  folder,
  actions,
  children,
}: {
  workspaceId: string;
  folder: Folder;
  actions: CanvasFolderActions;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useRailExpansion(`folder:${folder.id}`);
  const menu = useRailRowContextMenu();

  return (
    <div className="flex flex-col">
      {/* Only the header row is a drop target — not the expanded children — so
          dragging a canvas *out* of this folder isn't recaptured by it. */}
      <CanvasFolderDropTarget workspaceId={workspaceId} folderId={folder.id}>
        <div
          onContextMenu={menu.onContextMenu}
          className="group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 pr-8 hover:bg-n-05"
        >
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            data-state={open ? "open" : "closed"}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          >
            <RailDisclosureIcon
              open={open}
              icon={open ? FolderOpen : FolderIcon}
            />
            <span className="flex-1 truncate text-base text-n-80">
              {folder.name}
            </span>
          </button>
          <CanvasFolderMenu
            controller={menu}
            label={folder.name}
            onOpen={() => actions.onOpen(folder)}
            onNewCanvas={() => actions.onNewResource(folder.id)}
            onNewSubfolder={() => actions.onNewSubfolder(folder.id)}
            onRename={() => actions.onRename(folder)}
            onDelete={() => actions.onDelete(folder)}
          />
        </div>
      </CanvasFolderDropTarget>
      {open && <div className="flex flex-col pl-2">{children}</div>}
    </div>
  );
}

/**
 * Recursive canvas folder tree for one workspace, mirroring [`FilesRailTree`]:
 * renders the folders whose parent is `parentFolderId` (each containing its own
 * subtree) then the canvases filed directly under `parentFolderId`.
 * `renderCanvasRow` is supplied by the workspace row so per-canvas
 * handlers/permissions live in one place.
 */
export function WorkspacesRailCanvasFolderTree({
  workspaceId,
  parentFolderId,
  folders,
  canvases,
  renderCanvasRow,
  folderActions,
}: {
  workspaceId: string;
  parentFolderId: string | null;
  folders: Folder[];
  canvases: Canvas[];
  renderCanvasRow: (canvas: Canvas) => React.ReactNode;
  folderActions: CanvasFolderActions;
}) {
  const childFolders = useMemo(
    () =>
      folders
        .filter((f) => (f.parent_folder_id ?? null) === parentFolderId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [folders, parentFolderId],
  );

  const folderCanvases = useMemo(
    () => canvases.filter((c) => (c.folder_id ?? null) === parentFolderId),
    [canvases, parentFolderId],
  );

  return (
    <>
      {childFolders.map((folder) => (
        <CanvasFolderRow
          key={folder.id}
          workspaceId={workspaceId}
          folder={folder}
          actions={folderActions}
        >
          <WorkspacesRailCanvasFolderTree
            workspaceId={workspaceId}
            parentFolderId={folder.id}
            folders={folders}
            canvases={canvases}
            renderCanvasRow={renderCanvasRow}
            folderActions={folderActions}
          />
        </CanvasFolderRow>
      ))}
      {folderCanvases.map((canvas) => renderCanvasRow(canvas))}
    </>
  );
}
